import { apiRequest } from "../client";
import type { PlanFeesSnapshot } from "@/calculators/planFees/types";

const base = (faClientId: string) =>
  `/agentic-v2/fa-clients/${faClientId}/calculators/plan-fees`;

export async function getPlanFeesSnapshot(
  faClientId: string,
): Promise<PlanFeesSnapshot | null> {
  const res = await apiRequest<{ snapshot: PlanFeesSnapshot | null }>({
    url: base(faClientId),
    method: "GET",
  });
  return res.snapshot ?? null;
}

export type PlanFeesCompileTaskStatus = {
  taskId: string;
  status: string;
  error?: string | null;
  snapshot?: PlanFeesSnapshot | null;
};

export async function startPlanFeesCompileAsync(
  faClientId: string,
): Promise<{ taskId: string; status: string }> {
  return apiRequest<{ taskId: string; status: string }>({
    url: `${base(faClientId)}/compile/async`,
    method: "POST",
  });
}

export async function getPlanFeesCompileTask(
  faClientId: string,
  taskId: string,
): Promise<PlanFeesCompileTaskStatus> {
  return apiRequest<PlanFeesCompileTaskStatus>({
    url: `${base(faClientId)}/compile/tasks/${taskId}`,
    method: "GET",
  });
}

const POLL_MS = 2000;
const MAX_POLL_MS = 30 * 60 * 1000;

export async function compilePlanFeesSnapshot(
  faClientId: string,
  options?: { shouldContinue?: () => boolean },
): Promise<PlanFeesSnapshot> {
  const keepPolling = () => options?.shouldContinue?.() !== false;
  const { taskId } = await startPlanFeesCompileAsync(faClientId);
  const deadline = Date.now() + MAX_POLL_MS;
  while (Date.now() < deadline) {
    if (!keepPolling()) {
      throw new Error("Compile cancelled");
    }
    await new Promise((r) => setTimeout(r, POLL_MS));
    if (!keepPolling()) {
      throw new Error("Compile cancelled");
    }
    const task = await getPlanFeesCompileTask(faClientId, taskId);
    if (task.status === "failed" || task.status === "cancelled") {
      throw new Error(task.error || "Plan fees compile failed");
    }
    if (task.status === "completed") {
      if (!task.snapshot) {
        throw new Error("Compile finished without a snapshot");
      }
      return task.snapshot;
    }
  }
  throw new Error("Plan fees compile is still running — try refreshing in a minute.");
}

export async function savePlanFeesSnapshot(
  faClientId: string,
  snapshot: PlanFeesSnapshot,
): Promise<PlanFeesSnapshot> {
  const res = await apiRequest<{ snapshot: PlanFeesSnapshot }>({
    url: base(faClientId),
    method: "PUT",
    data: snapshot,
  });
  if (!res.snapshot) {
    throw new Error("Save returned no snapshot");
  }
  return res.snapshot;
}
